/**
 * 📄 문서 PostgreSQL + GCS 영구 저장 유틸리티
 * 
 * - 업로드 즉시: PostgreSQL에 문서 메타데이터 저장 + GCS에 PDF 원본 업로드
 * - 추출 완료 시: PostgreSQL에 추출 결과 저장 (upsert)
 * - 로그인/탭 포커스 시: PostgreSQL에서 문서 목록 복원
 * - 365일 보관 (파인튜닝 학습 데이터 축적 목적)
 */

// API 베이스 URL — App.js 와 동일 패턴
//   - localhost 개발: http://localhost:3002
//   - prod (docuai2.tlogin.net): '' (same-origin relative path → nginx → 3003)
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? `http://${window.location.hostname}:3002`
    : ''
);

/**
 * File 객체를 base64로 변환
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // data:...;base64, 제거
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 업로드 즉시 문서를 PostgreSQL에 저장 (status: 'pending')
 * PDF 원본도 GCS에 업로드 (50MB 이하)
 * @param {Object} params
 * @param {string} params.fileName - 파일명
 * @param {string} params.fileType - 파일 타입 (pdf, image 등)
 * @param {number} params.pageCount - 페이지 수
 * @param {string} params.templateType - 매칭된 템플릿
 * @param {File} params.file - File 객체 (PDF 업로드용)
 * @returns {Promise<Object|null>} { id, status } 또는 null
 */
export async function saveUploadToDB({ fileName, fileType, pageCount, templateType, file }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('📄 [DB Upload] 토큰 없음 — 비로그인 상태에서는 저장 불가');
      return null;
    }

    // PDF 원본을 base64로 변환 (50MB 이하)
    let pdfBase64 = null;
    if (file && file instanceof File && file.size <= 50 * 1024 * 1024) {
      try {
        pdfBase64 = await fileToBase64(file);
      } catch (e) {
        console.warn('📄 [DB Upload] base64 변환 실패:', e.message);
      }
    }

    const response = await fetch(`${API_BASE_URL}/api/documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        originalFilename: fileName,
        fileType: fileType || 'pdf',
        templateType: templateType || '미분류',
        pageCount: pageCount || 1,
        pdfBase64,
      }),
    });

    const result = await response.json();
    if (result.success) {
      console.log(`✅ [DB Upload] ${fileName} → PostgreSQL 저장 (ID: ${result.document.id})`);
      return { id: result.document.id, status: result.document.status };
    } else {
      console.warn(`⚠️ [DB Upload] ${fileName} 저장 실패:`, result.error);
      return null;
    }
  } catch (err) {
    console.warn(`⚠️ [DB Upload] ${fileName} 저장 오류:`, err.message);
    return null;
  }
}

/**
 * DB에서 최신 문서 목록을 가져와 fileList 형식으로 반환
 * 탭 포커스 시 호출하여 크로스 브라우저 동기화
 * @returns {Promise<Array>} fileList 형식의 문서 배열
 */
export async function refreshDocuments() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const response = await fetch(`${API_BASE_URL}/api/documents/restore/all`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const result = await response.json();

    if (!result.success || !result.files || result.files.length === 0) {
      return [];
    }

    // 백엔드 형식 → fileList 형식 변환
    const files = result.files.map((doc) => {
      const pageResults = (doc.extractedItems || [])
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .map(item => ({
          pageNumber: item.pageNumber,
          extractedData: item.extractedData || {},
          items: item.extractedData?.items || [],
          boundingBoxes: [],
          flashData: item.extractedData || {},
          validationStatus: item.validationStatus,
        }));

      const firstPage = pageResults[0];
      const extractedData = firstPage?.extractedData || {};

      return {
        id: doc.id,
        name: doc.name,
        originalName: doc.name,
        isPDF: doc.fileType === 'pdf',
        status: doc.status || 'pending',
        matchedTemplate: doc.templateType || '미분류',
        totalPages: doc.pageCount || pageResults.length || 1,
        pageResults: pageResults.length > 0 ? pageResults : null,
        extractedData: pageResults.length > 0 ? extractedData : null,
        flashData: pageResults.length > 0 ? extractedData : null,
        items: extractedData.items || [],
        _restoredFromDB: true,
        _documentId: doc.id,
        _hasPdf: doc.hasPdf,
        _gcsPath: doc.gcsPath,
        _uploadedAt: doc.uploadedAt,
        _uploadDate: doc.uploadDate,
        preview: null,
        file: null,
      };
    });

    console.log(`🔄 [DB Refresh] ${files.length}건 문서 (completed: ${files.filter(f => f.status === 'completed').length}건)`);
    return files;
  } catch (err) {
    console.warn('⚠️ [DB Refresh] 문서 조회 실패:', err.message);
    return [];
  }
}

/**
 * 추출 완료된 파일의 결과를 PostgreSQL에 저장 + PDF를 GCS에 업로드
 * @param {Object} file - fileList의 파일 객체
 * @param {Object} options - 추가 옵션
 * @returns {Promise<Object|null>} 저장된 문서 정보 또는 null (실패 시)
 */
export async function saveExtractionToDB(file, options = {}) {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('📄 [DB Save] 토큰 없음 — 비로그인 상태에서는 저장 불가');
      return null;
    }

    if (!file || !file.name) {
      console.warn('📄 [DB Save] 파일 정보 없음');
      return null;
    }

    // pageResults에서 저장할 데이터 추출
    const pageResults = (file.pageResults || []).map(pr => ({
      pageNumber: pr.pageNumber || 1,
      extractedData: pr.extractedData || pr.flashData || {},
      items: pr.items || (pr.extractedData?.items) || [],
      boundingBoxes: pr.boundingBoxes || [],
      confidence: pr.confidence || null,
    }));

    // pageResults가 없으면 extractedData에서 단일 페이지로 구성
    if (pageResults.length === 0 && file.extractedData) {
      pageResults.push({
        pageNumber: 1,
        extractedData: file.extractedData,
        items: file.extractedData?.items || file.items || [],
        boundingBoxes: file.extractedData?.boundingBoxes || [],
        confidence: null,
      });
    }

    if (pageResults.length === 0) {
      console.warn('📄 [DB Save] 저장할 추출 결과 없음:', file.name);
      return null;
    }

    // PDF 원본을 base64로 변환 (File 객체가 있는 경우만)
    let pdfBase64 = null;
    if (file.file && file.file instanceof File) {
      try {
        // 50MB 이하만 업로드 (너무 큰 파일 방지)
        if (file.file.size <= 50 * 1024 * 1024) {
          pdfBase64 = await fileToBase64(file.file);
          console.log(`📄 [DB Save] PDF base64 준비: ${file.name} (${(file.file.size / 1024).toFixed(0)}KB)`);
        } else {
          console.warn(`📄 [DB Save] PDF 파일이 50MB 초과하여 GCS 업로드 생략: ${file.name}`);
        }
      } catch (e) {
        console.warn('📄 [DB Save] PDF base64 변환 실패 (추출 결과만 저장):', e.message);
      }
    }

    const payload = {
      fileName: file.name || file.originalName || 'unknown',
      fileType: file.isPDF ? 'pdf' : (file.file?.type || 'unknown'),
      templateType: file.matchedTemplate || options.templateType || '미분류',
      pageCount: file.totalPages || pageResults.length,
      pageResults,
      pdfBase64,
      metadata: {
        extractedAt: new Date().toISOString(),
        ...(options.metadata || {}),
      },
    };

    const response = await fetch(`${API_BASE_URL}/api/documents/save-extraction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success) {
      console.log(`✅ [DB Save] ${file.name} → PostgreSQL 저장 완료 (${result.document?.pageCount || 0} 페이지, GCS: ${result.document?.gcsPath ? '✅' : '❌'}, ID: ${result.document?.id})`);
      return result.document;
    } else {
      console.warn(`⚠️ [DB Save] ${file.name} 저장 실패:`, result.error);
      return null;
    }
  } catch (err) {
    // PostgreSQL 연결 실패 시에도 앱은 정상 동작해야 함
    console.warn(`⚠️ [DB Save] ${file?.name || 'unknown'} 저장 오류 (무시):`, err.message);
    return null;
  }
}

/**
 * 로그인 시 사용자의 최근 문서 + 추출 결과 복원
 * @returns {Promise<Array>} fileList 형식의 문서 배열
 */
export async function restoreDocuments() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const response = await fetch(`${API_BASE_URL}/api/documents/restore/all`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const result = await response.json();

    if (!result.success || !result.files || result.files.length === 0) {
      console.log('📄 [DB Restore] 복원할 문서 없음');
      return [];
    }

    // 백엔드 형식 → fileList 형식 변환
    const files = result.files.map((doc, index) => {
      // extracted_items → pageResults 변환
      const pageResults = (doc.extractedItems || [])
        .sort((a, b) => a.pageNumber - b.pageNumber)
        .map(item => ({
          pageNumber: item.pageNumber,
          extractedData: item.extractedData || {},
          items: item.extractedData?.items || [],
          boundingBoxes: [],
          flashData: item.extractedData || {},
          validationStatus: item.validationStatus,
        }));

      // 첫 페이지의 extractedData를 문서 레벨로도 설정
      const firstPage = pageResults[0];
      const extractedData = firstPage?.extractedData || {};

      // ── isPDF 자동 감지: fileType 또는 파일명 기반 (옛날 silent-fail 데이터 호환) ──
      //    명시적 'pdf' 또는 .pdf 확장자만 true. 미상 파일(fileType=='', 확장자 없음)은 false.
      const _name = (doc.name || '').toLowerCase();
      const _ft = (doc.fileType || '').toLowerCase();
      const _isOffice = _ft === 'xlsx' || _ft === 'xls' || _ft === 'xlsm' || _ft === 'docx' || _ft === 'doc'
                     || /\.(xlsx|xlsm|xls|docx|doc)$/i.test(_name);
      const _isImage = _ft === 'image' || /\.(png|jpe?g|tiff?|bmp|webp|gif)$/i.test(_name);
      const _isPdfNow = !_isOffice && !_isImage && (_ft === 'pdf' || /\.pdf$/i.test(_name));

      return {
        id: doc.id,
        name: doc.name,
        originalName: doc.name,
        fileType: _ft,                         // ★ xlsx 분기에서 사용 (확장자 truncated 대비)
        isPDF: _isPdfNow,                      // ★ Office/이미지가 PDF 분기로 가지 않게
        status: doc.status || 'completed',
        matchedTemplate: doc.templateType || '미분류',
        totalPages: doc.pageCount || pageResults.length || 1,
        pageResults,
        extractedData,
        flashData: extractedData,
        items: extractedData.items || [],
        // DB 복원 플래그
        _restoredFromDB: true,
        _documentId: doc.id,
        _hasPdf: doc.hasPdf,
        _gcsPath: doc.gcsPath,
        _uploadedAt: doc.uploadedAt,
        _uploadDate: doc.uploadDate,
        // preview는 PDF 다운로드 후 설정
        preview: null,
        file: null,
      };
    });

    console.log(`✅ [DB Restore] ${files.length}건 문서 복원 (PDF 있음: ${files.filter(f => f._hasPdf).length}건)`);
    return files;
  } catch (err) {
    console.warn('⚠️ [DB Restore] 문서 복원 실패:', err.message);
    return [];
  }
}

/**
 * 문서의 PDF 다운로드 URL (서버 프록시)
 * @param {string} documentId - 문서 UUID
 * @returns {string} PDF 다운로드 URL
 */
export function getPdfDownloadUrl(documentId) {
  return `${API_BASE_URL}/api/documents/${documentId}/pdf`;
}

/**
 * 복원된 문서의 PDF를 GCS에서 다운로드하여 File 객체로 반환
 * (PDF 뷰어에서 사용 가능한 형태)
 * @param {string} documentId - 문서 UUID
 * @param {string} fileName - 파일명
 * @returns {Promise<File|null>} File 객체 또는 null
 */
export async function fetchRestoredPdf(documentId, fileName) {
  try {
    const token = localStorage.getItem('token');
    if (!token || !documentId) return null;

    console.log(`📥 [GCS Download] PDF 다운로드 시작: ${fileName}`);
    const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) {
      console.warn(`⚠️ [GCS Download] 실패: ${response.status}`);
      return null;
    }

    const blob = await response.blob();
    const safeName = (fileName || 'restored').replace(/\.pdf$/i, '') + '.pdf';
    const file = new File([blob], safeName, { type: 'application/pdf' });
    console.log(`✅ [GCS Download] PDF 다운로드 완료: ${fileName} (${(blob.size / 1024).toFixed(0)}KB)`);
    return file;
  } catch (err) {
    console.warn(`⚠️ [GCS Download] PDF 다운로드 오류:`, err.message);
    return null;
  }
}

/**
 * DB에서 문서 삭제 (PostgreSQL + GCS)
 * @param {Array<string>} documentIds - 삭제할 문서 UUID 배열
 * @param {Array<string>} fileNames - 삭제할 파일명 배열 (안전망: _documentId 없는 파일용)
 * @returns {Promise<number>} 삭제된 문서 수
 */
export async function deleteDocumentsFromDB(documentIds = [], fileNames = []) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 0;
    if (documentIds.length === 0 && fileNames.length === 0) return 0;

    const body = {};
    if (documentIds.length > 0) body.documentIds = documentIds;
    if (fileNames.length > 0) body.fileNames = fileNames;

    const response = await fetch(`${API_BASE_URL}/api/documents/bulk`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const result = await response.json();
    if (result.success) {
      console.log(`🗑️ [DB Delete] ${result.deletedCount}건 문서 삭제 완료 (ids: ${documentIds.length}, names: ${fileNames.length})`);
      return result.deletedCount;
    } else {
      console.warn('⚠️ [DB Delete] 삭제 실패:', result.error);
      return 0;
    }
  } catch (err) {
    console.warn('⚠️ [DB Delete] 삭제 오류:', err.message);
    return 0;
  }
}

/**
 * 사용자의 오늘 문서 목록 조회
 * @returns {Promise<Array>} 문서 배열
 */
export async function getTodayDocuments() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const response = await fetch(`${API_BASE_URL}/api/documents/today`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const result = await response.json();
    return result.success ? result.documents : [];
  } catch (err) {
    console.warn('⚠️ [DB Load] 오늘 문서 조회 실패:', err.message);
    return [];
  }
}

/**
 * 사용자의 이전(1~7일전) 문서 목록 조회
 * @returns {Promise<Object>} { documents, grouped }
 */
export async function getPreviousDocuments() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return { documents: [], grouped: {} };

    const response = await fetch(`${API_BASE_URL}/api/documents/previous`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const result = await response.json();
    return result.success ? { documents: result.documents, grouped: result.grouped } : { documents: [], grouped: {} };
  } catch (err) {
    console.warn('⚠️ [DB Load] 이전 문서 조회 실패:', err.message);
    return { documents: [], grouped: {} };
  }
}
